/*
 * The marketplace page's words, per store. India's are the page as it has
 * always read. Every other store (US, the Gulf) sells a different catalogue,
 * pays by card through Stripe, gets no Fleek coins or GST, and is fulfilled by
 * hand within 24 hours (backend marketplace/market.py DELIVERY_MESSAGE), so its
 * copy says exactly that and names only brands that store sells.
 *
 * The hero never states one delivery method: the same brand arrives as a code
 * or by direct activation depending on the plan and the day's supply.
 */

export const INDIA_FAQ = [
  {
    q: "Are these official subscriptions?",
    a: "Yes. Every plan on Fleek is sourced through an authorised partnership with the brand or its distributor. You get a genuine subscription on your own account, with the same features and support you would get buying direct.",
  },
  {
    q: "How fast do I get access?",
    a: "Most plans activate within seconds of payment. Your details arrive by email and WhatsApp straight away. A small number of brands need manual provisioning and can take a few hours — the plan page tells you before you pay.",
  },
  {
    q: "What are Fleek coins?",
    a: "Coins are Fleek's reward currency. You earn them on orders and can spend them to bring down the price of a future purchase. Your balance sits in the header once you are signed in.",
  },
  {
    q: "Can I use this on an existing account?",
    a: "Usually yes. Most subscriptions apply to the account you already use, so your watchlist, history and settings stay where they are. Where a brand requires a fresh account, the plan page says so upfront.",
  },
  {
    q: "What if something doesn't work?",
    a: "Message us on WhatsApp and a human will pick it up. If a plan cannot be activated we refund you in full — we would rather return the money than leave you with something you cannot use.",
  },
  {
    q: "Do I need the app to buy?",
    a: "No. You can buy everything here on the web. The app is for afterwards: it finds subscriptions hiding in your bank and email, warns you before renewals, and lets you cancel with one toggle.",
  },
];

export const ABROAD_FAQ = [
  INDIA_FAQ[0],
  {
    q: "How fast do I get access?",
    a: "Within 24 hours of payment. Your plan details arrive by email, and the order page shows them too.",
  },
  INDIA_FAQ[3],
  {
    q: "What if something doesn't work?",
    a: "Write to us from the Support page and a person will pick it up. If a plan cannot be activated we refund you in full — we would rather return the money than leave you with something you cannot use.",
  },
  {
    q: "How do I pay?",
    a: "By card, through a secure checkout in your store's currency. You see the full price before you pay.",
  },
];

export const INDIA_STEPS = [
  {
    title: "Pick your plan",
    body: "Compare every duration and tier side by side, with the real price after discount — no coupon hunting.",
  },
  {
    title: "Pay in ten seconds",
    body: "UPI, cards, netbanking and wallets. One checkout, and you see the price before you make an account.",
  },
  {
    title: "Activate, then forget it",
    body: "Details arrive instantly by email and WhatsApp. Renewal reminders and one-toggle cancellation come free in the app.",
  },
];

export const ABROAD_STEPS = [
  INDIA_STEPS[0],
  {
    title: "Pay by card",
    body: "One secure checkout in your store's currency, and you see the full price before you pay.",
  },
  {
    title: "Get it within 24 hours",
    body: "Your plan details arrive by email within 24 hours: a code or direct activation, depending on the plan.",
  },
];

export const INDIA_STATS = [
  { value: "6M+", label: "users" },
  { value: "66%", label: "max discount" },
  { value: "24/7", label: "WhatsApp support" },
];

const list = (names) =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

const ABROAD_PROMISE = "genuine plans, delivered by code or straight to your account, depending on the plan.";

/**
 * Words for the store. ``brands`` are the names this store has loaded (in
 * display order), ``examples`` the few to name (./heroBrands.js
 * exampleBrandNames: one per product family, subscriptions first; ``brands``
 * when not given), ``brandCount`` how many brands the whole store lists (the
 * listing's ``count``; the loaded page is only the first 30) and
 * ``maxDiscount`` the best discount the hero shows, so the hero never names a
 * brand or a figure the store can't back up. With no ``brandCount`` the
 * "N brands" stat is left out rather than counting the loaded page.
 *
 * India's figures are fixed: 66% is the best discount India sells today. Its
 * hero states no activation time.
 */
export const copyFor = ({ india, brands = [], examples, brandCount = null, maxDiscount = 0 }) => {
  if (india) {
    return {
      india: true,
      accent: "Up to 66% off.",
      sub: "Amazon Prime, Sony LIV, Microsoft 365, Kaspersky, Livemint and more — verified sellers, coins back on every order.",
      stats: INDIA_STATS,
      steps: INDIA_STEPS,
      faq: INDIA_FAQ,
      dealTrust: "UPI, cards & wallets · secure checkout",
      pricesNote: "GST included",
    };
  }
  const named = (examples || brands).filter(Boolean).slice(0, 4);
  const pct = Math.round(Number(maxDiscount) || 0);
  const total = Math.floor(Number(brandCount)) || 0;
  const stats = [];
  if (total > 0) stats.push({ value: String(total), label: total === 1 ? "brand" : "brands" });
  if (pct > 0) stats.push({ value: `${pct}%`, label: "max discount" });
  stats.push({ value: "Card", label: "secure checkout" });
  return {
    india: false,
    accent: pct > 0 ? `Up to ${pct}% off.` : "For less.",
    sub: named.length
      ? `${Math.max(total, brands.length) > named.length ? `${named.join(", ")} and more` : list(named)} — ${ABROAD_PROMISE}`
      : `G${ABROAD_PROMISE.slice(1)}`,
    stats,
    steps: ABROAD_STEPS,
    faq: ABROAD_FAQ,
    dealTrust: "Pay by card · secure checkout",
    pricesNote: null,
  };
};
